import { useEffect, useState } from "react";
import { Radio, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fieldInputCls } from "@/components/Field";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";

const SearchBox = ({ onPick }) => {
  const { t } = useT();
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (q.trim().length < 3) { setRes([]); return undefined; }
    const id = setTimeout(() => api.get("/weather/search", { params: { q } }).then((r) => { setRes(r.data); setErr(""); }).catch((e) => setErr(formatApiError(e))), 400);
    return () => clearTimeout(id);
  }, [q]);
  return (
    <div className="relative w-full sm:w-64">
      <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
      <Input data-testid="weather-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("wx_search")} className={`${fieldInputCls} pl-9`} />
      {(res.length > 0 || err) && (
        <div data-testid="weather-search-results" className="absolute z-30 mt-1 w-full bg-[#12141C] border border-[#282D3D] rounded-sm shadow-xl">
          {err && <div className="px-3 py-2 font-mono text-xs text-[#FF3B30]">{err}</div>}
          {res.map((r, i) => (
            <button key={`${r.lat}-${r.lon}`} type="button" data-testid={`weather-search-result-${i}`} onClick={() => { onPick({ mode: "manual", name: r.name, lat: r.lat, lon: r.lon }); setQ(""); setRes([]); }}
              className="w-full text-left px-3 py-2 hover:bg-[#181B26] font-mono text-xs">
              {r.name}<span className="text-slate-500"> · {[r.state, r.country].filter(Boolean).join(", ")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const PlacePicker = ({ places, choice, setChoice }) => {
  const { t } = useT();
  const live = places?.live || [];
  const tracks = places?.tracks || [];
  const trackVal = choice.mode === "manual" ? tracks.find((x) => x.lat === choice.lat && x.lon === choice.lon)?.id || "" : "";
  return (
    <div data-testid="weather-place-picker" className="flex flex-wrap items-center gap-2">
      <button type="button" data-testid="weather-auto-live-btn" onClick={() => setChoice({ mode: "auto" })}
        className={`h-10 flex items-center gap-1.5 px-3 rounded-sm border font-heading uppercase tracking-wide transition-colors duration-200 ${choice.mode === "auto" ? "border-[#FF3B30] bg-[#FF3B30]/15 text-white" : "border-[#282D3D] text-slate-400 hover:text-white"}`}>
        <Radio className="w-4 h-4 text-[#FF3B30]" /> {t("wx_auto_live")}{live.length > 0 && <span className="font-mono text-[10px] text-[#FF3B30]">({live.length})</span>}
      </button>
      <Select value={trackVal} onValueChange={(id) => { const tr = tracks.find((x) => x.id === id); if (tr) setChoice({ mode: "manual", name: tr.name, lat: tr.lat, lon: tr.lon }); }}>
        <SelectTrigger data-testid="weather-track-select" className={`${fieldInputCls} w-full sm:w-56`}>
          <SelectValue placeholder={t("wx_pick_track")} />
        </SelectTrigger>
        <SelectContent>
          {tracks.map((tr) => <SelectItem key={tr.id} value={tr.id} data-testid={`weather-track-option-${tr.id}`}>{tr.name}{tr.city ? ` · ${tr.city}` : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <SearchBox onPick={setChoice} />
    </div>
  );
};
